import { computed, ref, useStore } from "@nuxtjs/composition-api";
import { defineStore } from "pinia";

import { getLtksStore } from "@/store/ltks";
import { EXPERIMENTS_DSML_CONSUMER_SEARCH, FEATURE_FLAG_ACTIVE } from "@/utils/experiments/constants";
import mapData from "@/utils/transform/map-data";

import useConsumerSearchApi from "./-api";
import { getExperimentsStore } from "../experiments";
import { getProfilesStore } from "../profiles";

interface RetailerProductLinksStoreContext {
  $api: ReturnType<typeof useConsumerSearchApi>;
}

export const useConsumerSearchStore = defineStore(
  "consumer-search",
  () => {
    const store = useStore();
    const ltksStore = getLtksStore(store);
    const profilesStore = getProfilesStore(store);
    const experimentsStore = getExperimentsStore(store);

    // State
    const ltksMap = ref<ConsumerSearchPostIDResultMap>({});
    const productsMap = ref<ConsumerSearchProductIDResultMap>({});
    const profilesMap = ref<ConsumerSearchProfileIDResultMap>({});
    const ltks = ref<ConsumerSearchResponsePostID[]>([]);
    const ltksTotal = ref<number>(0);
    const ltksComplete = ref<boolean>(false);
    const profiles = ref<ConsumerSearchResponseProfileID[]>([]);
    const profilesTotal = ref<number>(0);
    const profilesComplete = ref<boolean>(false);

    const isDSMLExperimentEnabled = computed(
      () => experimentsStore.variant(EXPERIMENTS_DSML_CONSUMER_SEARCH)?.value === FEATURE_FLAG_ACTIVE,
    );

    // Actions
    async function fetchLtkSearchResults(
      this: RetailerProductLinksStoreContext,
      data: Omit<PostConsumerSearchRequest, "types">,
    ) {
      resetLtks();
      const response = await this.$api.fetchLtkIDSearchResults({
        ...data,
        ...(isDSMLExperimentEnabled.value && {
          searchParams: {
            ...data.searchParams,
            model: "dsmlImage",
          },
        }),
      });
      const newLtks = response.data.items;
      ltks.value = newLtks;
      ltksTotal.value = response.data.total;
      ltksComplete.value = response.data.complete;
      ltksMap.value = Object.assign({}, ltksMap.value, mapData("id", newLtks));
      productsMap.value = Object.assign(
        {},
        productsMap.value,
        mapData(
          "id",
          newLtks.flatMap((item) => item.children || []),
        ),
      );
      if (process.server) {
        await ltksStore.fetchLtksByIds(newLtks.map((item) => item.id));
      }
    }

    async function fetchMoreLtkSearchResults(
      this: RetailerProductLinksStoreContext,
      data: Omit<PostConsumerSearchRequest, "types">,
    ) {
      const response = await this.$api.fetchLtkIDSearchResults({
        ...data,
        ...(isDSMLExperimentEnabled.value && {
          searchParams: {
            ...data.searchParams,
            model: "dsmlImage",
          },
        }),
      });
      const newLtks = response.data.items;
      await ltksStore.fetchLtksByIds(newLtks.map((item) => item.id));
      ltks.value = [...ltks.value, ...newLtks];
      ltksTotal.value = response.data.total;
      ltksComplete.value = response.data.complete;
      ltksMap.value = Object.assign({}, ltksMap.value, mapData("id", newLtks));
      productsMap.value = Object.assign(
        {},
        productsMap.value,
        mapData(
          "id",
          newLtks.flatMap((item) => item.children || []),
        ),
      );
    }

    // Actions
    async function fetchProfileSearchResults(
      this: RetailerProductLinksStoreContext,
      data: Omit<PostConsumerSearchRequest, "types">,
    ) {
      resetProfiles();
      const response = await this.$api.fetchProfileIDSearchResults(data);
      const newProfiles = response.data.items;
      profiles.value = newProfiles;
      await profilesStore.fetchProfilesByIds({ ids: newProfiles.map((item) => item.id) });
      profilesTotal.value = response.data.total;
      profilesComplete.value = response.data.complete;
      profilesMap.value = Object.assign({}, profilesMap.value, mapData("id", newProfiles));
    }

    async function fetchMoreProfileSearchResults(
      this: RetailerProductLinksStoreContext,
      data: Omit<PostConsumerSearchRequest, "types">,
    ) {
      const response = await this.$api.fetchProfileIDSearchResults(data);
      const newProfiles = response.data.items;
      await profilesStore.fetchProfilesByIds({ ids: newProfiles.map((item) => item.id) });
      profiles.value = [...profiles.value, ...newProfiles];
      profilesTotal.value = response.data.total;
      profilesComplete.value = response.data.complete;
      profilesMap.value = Object.assign({}, profilesMap.value, mapData("id", newProfiles));
    }

    function resetLtks() {
      ltks.value = [];
      ltksTotal.value = 0;
      ltksComplete.value = false;
    }

    function resetProfiles() {
      profiles.value = [];
      profilesTotal.value = 0;
      profilesComplete.value = false;
    }

    function resetStore() {
      ltks.value = [];
      ltksTotal.value = 0;
      ltksComplete.value = false;
      profiles.value = [];
      profilesTotal.value = 0;
      profilesComplete.value = false;
    }

    return {
      ltksMap,
      productsMap,
      profilesMap,
      ltks,
      ltksTotal,
      ltksComplete,
      profiles,
      profilesTotal,
      profilesComplete,
      fetchLtkSearchResults,
      fetchMoreLtkSearchResults,
      fetchProfileSearchResults,
      fetchMoreProfileSearchResults,
      resetLtks,
      resetProfiles,
      resetStore,
    };
  },
  {
    api: useConsumerSearchApi,
  },
);
